import { Icon } from '../components/Icon.js';
import type { InboxItem } from '../fixtures/domain.js';
import { plural } from '../util/format.js';
import { useDismiss } from '../util/useDismiss.js';
import { ListPicker, type Filing } from './ListPicker.js';
import { ScopeBox } from './ScopeBox.js';
import type { ConversationList } from './useLists.js';
import styles from './AddToListDialog.module.css';

interface AddToListDialogProps {
  /** The chats being filed, in list order. */
  targets: InboxItem[];
  /** A list was pressed and the chats moved — the dialog is done at that point. */
  onApplied: (list: ConversationList, direction: Filing) => void;
  onClose: () => void;
}

/**
 * Filing a selection, as its own screen: the faces (the part a bulk action gets
 * wrong) with the picker along the box's foot. No footer — the picker applies on
 * the press, so there's nothing to Cancel or confirm; an "×" to leave is all it
 * needs.
 */
export function AddToListDialog({ targets, onApplied, onClose }: AddToListDialogProps) {
  useDismiss(true, onClose);
  const count = targets.length;

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <div className={styles.heading}>
            <h2 className={styles.title}>Add {plural(count, 'conversation')} to a list</h2>
            <p className={styles.subtitle}>Picking a list files them and opens it.</p>
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <Icon name="close" size={20} />
          </button>
        </div>

        <div className={styles.body}>
          <ScopeBox
            targets={targets}
            foot={
              <ListPicker
                channelIds={targets.map((item) => item.channel.id)}
                defaultOpen
                onApplied={onApplied}
              />
            }
          />
        </div>
      </div>
    </div>
  );
}
